import { FloatingArrow } from '@floating-ui/react';
import cn from 'classnames';
import { CSSProperties, MutableRefObject, useMemo } from 'react';

import { SmartFloatingPortal as FloatingPortal } from '@/shared/ui/SmartFloatingPortal';

import { useTypedSelector } from 'store';
import {
  addBignumbers,
  bn,
  divideBignumbers,
  humanizeBn,
  multiplyBignumbers,
  subtractBignumbers,
} from 'tools';
import { formatToken } from 'utils/formats/formatToken';
import { normalizeNumber } from 'utils/formats/normalizeNumber';

import type { OrderbookSide, ProcessedOrderbookEntry } from '../model/types';

export interface OrderbookTooltipData {
  avgPrice: string;
  volume: string;
  total: string;
  ordersCount: number | null;
  ordersAmount: string | null;
  ordersAmountQuote: string | null;
  percentCount: number;
  percentageByLastPrice: string;
}

export interface OrderbookHoverTooltipProps {
  hoveredSide: OrderbookSide | null;
  hoveredIndex: number | null;
  asks: ProcessedOrderbookEntry[];
  bids: ProcessedOrderbookEntry[];
  isScrolling: boolean;
  floatingStyles: CSSProperties;
  context: unknown;
  setFloating: (node: HTMLElement | null) => void;
  arrowRef: MutableRefObject<SVGSVGElement | null>;
  lastPrice: string;
  baseSymbol: string;
  quoteSymbol: string;
}

const CALC_DECIMALS = 18;

function calculateTooltipData(
  side: OrderbookSide,
  index: number,
  asks: ProcessedOrderbookEntry[],
  bids: ProcessedOrderbookEntry[],
  lastPrice: string,
): OrderbookTooltipData | null {
  const isAsk = side === 'ask';
  const sourceArray = isAsk ? asks : bids;

  if (index < 0 || index >= sourceArray.length) {
    return null;
  }

  // Both arrays are [high→low] (descending price order)
  // Visual highlight logic:
  //   - Asks: highlights index >= hoveredIndex (from hovered to END, toward spread)
  //   - Bids: highlights index <= hoveredIndex (from START to hovered, toward spread)
  // Tooltip must match visual highlight range:
  //   - Asks: slice(index) captures from hovered to END (toward lower prices/spread)
  //   - Bids: slice(0, index+1) captures from START to hovered (toward spread)
  const array = isAsk ? sourceArray.slice(index) : sourceArray.slice(0, index + 1);

  if (array.length === 0) {
    return {
      avgPrice: '0',
      volume: '0',
      total: '0',
      ordersCount: null,
      ordersAmount: null,
      ordersAmountQuote: null,
      percentCount: 0,
      percentageByLastPrice: '+0.00%',
    };
  }

  const { totalBn, volumeBn } = array.reduce(
    (acc, val) => {
      if (val.isPlaceholder) return acc;

      const priceBn = bn(val.price, CALC_DECIMALS);
      const amountBn = bn(val.amount, CALC_DECIMALS);

      acc.volumeBn = addBignumbers([acc.volumeBn, CALC_DECIMALS], [amountBn, CALC_DECIMALS]);

      const priceTimesAmount = multiplyBignumbers(
        [priceBn, CALC_DECIMALS],
        [amountBn, CALC_DECIMALS],
      );
      acc.totalBn = addBignumbers([acc.totalBn, CALC_DECIMALS], [priceTimesAmount, CALC_DECIMALS]);

      return acc;
    },
    { volumeBn: bn(0, CALC_DECIMALS), totalBn: bn(0, CALC_DECIMALS) },
  );

  // The hovered entry position depends on slice direction:
  //   - Asks: slice(index) puts hovered at array[0]
  //   - Bids: slice(0, index+1) puts hovered at array[array.length - 1]
  const targetEntry = isAsk ? array[0] : array[array.length - 1];
  const price = targetEntry?.price ?? '0';

  // Calculate precision from the target entry's price decimals
  const pricePrecision = price.split('.')?.[1]?.length ?? 3;
  const precision = pricePrecision + 1;

  // Check if we have order data from our API (null means API data not available)
  const hasOrderData =
    targetEntry?.ordersCurrentCount !== undefined && targetEntry?.ordersCurrentCount !== null;

  const ordersCount = hasOrderData ? (targetEntry?.ordersCurrentCount ?? 0) : null;
  const ordersAmountRaw = hasOrderData ? (targetEntry?.ordersCurrentAmount ?? 0) : null;
  const ordersAmountQuoteRaw = hasOrderData ? (targetEntry?.ordersCurrentAmountQuote ?? 0) : null;

  const ordersAmount =
    ordersAmountRaw !== null ? normalizeNumber(Math.max(0, ordersAmountRaw)) : null;
  const ordersAmountQuote =
    ordersAmountQuoteRaw !== null ? normalizeNumber(Math.max(0, ordersAmountQuoteRaw)) : null;

  const avgPriceBn = divideBignumbers([totalBn, CALC_DECIMALS], [volumeBn, CALC_DECIMALS]);
  const avgPriceStr = humanizeBn(avgPriceBn, CALC_DECIMALS);

  const priceBn = bn(price, CALC_DECIMALS);
  const lastPriceBn = bn(lastPrice || '0', CALC_DECIMALS);
  const priceDiffBn = subtractBignumbers([priceBn, CALC_DECIMALS], [lastPriceBn, CALC_DECIMALS]);

  let percent = 0;
  if (Number(lastPrice) > 0) {
    const percentBn = divideBignumbers([priceDiffBn, CALC_DECIMALS], [lastPriceBn, CALC_DECIMALS]);
    percent = Number(humanizeBn(percentBn, CALC_DECIMALS)) * 100;
  }

  const avgPriceFormatted = normalizeNumber(Number(avgPriceStr).toFixed(precision));

  return {
    avgPrice: avgPriceFormatted,
    volume: normalizeNumber(humanizeBn(volumeBn, CALC_DECIMALS)),
    total: normalizeNumber(humanizeBn(totalBn, CALC_DECIMALS)),
    ordersCount,
    ordersAmount,
    ordersAmountQuote,
    percentCount: percent,
    percentageByLastPrice: `${percent >= 0 ? '+' : ''}${percent.toFixed(2)}%`,
  };
}

export function OrderbookHoverTooltip({
  hoveredSide,
  hoveredIndex,
  asks,
  bids,
  isScrolling,
  floatingStyles,
  context,
  setFloating,
  arrowRef,
  lastPrice,
  baseSymbol,
  quoteSymbol,
}: OrderbookHoverTooltipProps) {
  const isAdmin = useTypedSelector(store => store.auth.isAdmin);

  const tooltipData = useMemo(() => {
    if (hoveredSide === null || hoveredIndex === null) return null;
    return calculateTooltipData(hoveredSide, hoveredIndex, asks, bids, lastPrice);
  }, [hoveredSide, hoveredIndex, asks, bids, lastPrice]);

  const isVisible = hoveredSide !== null && hoveredIndex !== null && tooltipData && !isScrolling;

  if (!isVisible) {
    return null;
  }

  return (
    <FloatingPortal>
      <div
        ref={setFloating}
        className="bg-black text-white py-1.5 px-2.5 rounded-md text-xs pointer-events-none whitespace-nowrap z-[999] w-[200px] border border-zinc-800 shadow-lg"
        style={floatingStyles}
      >
        <div className="flex w-full items-center justify-between py-0.5">
          <span className="text-zinc-400">Avg price:</span>
          <span className="font-mono">{formatToken(bn(tooltipData.avgPrice))}</span>
        </div>
        <div className="flex w-full items-center justify-between py-0.5">
          <span className="text-zinc-400">Sum {baseSymbol}:</span>
          <span className="font-mono">{formatToken(bn(tooltipData.volume))}</span>
        </div>
        <div className="flex w-full items-center justify-between py-0.5">
          <span className="text-zinc-400">Sum {quoteSymbol}:</span>
          <span className="font-mono">{formatToken(bn(tooltipData.total))}</span>
        </div>

        {isAdmin && (
          <>
            <div className="flex w-full items-center justify-between py-0.5">
              <span className="text-zinc-400">Our orders count:</span>
              <span className="font-mono">
                {tooltipData.ordersCount !== null ? tooltipData.ordersCount : '—'}
              </span>
            </div>
            <div className="flex w-full items-center justify-between py-0.5">
              <span className="text-zinc-400">Our {baseSymbol} sum:</span>
              <span className="font-mono">
                {tooltipData.ordersAmount !== null
                  ? formatToken(bn(tooltipData.ordersAmount))
                  : '—'}
              </span>
            </div>
            <div className="flex w-full items-center justify-between py-0.5">
              <span className="text-zinc-400">Our {quoteSymbol} sum:</span>
              <span className="font-mono">
                {tooltipData.ordersAmountQuote !== null
                  ? formatToken(bn(tooltipData.ordersAmountQuote))
                  : '—'}
              </span>
            </div>
          </>
        )}

        <div className="flex w-full items-center justify-between py-0.5">
          <span className="text-zinc-400">Price changes %:</span>
          <span
            className={cn('font-mono', {
              'text-[#f23645]': tooltipData.percentCount < 0,
              'text-[#089981]': tooltipData.percentCount >= 0,
            })}
          >
            {tooltipData.percentageByLastPrice}
          </span>
        </div>

        <FloatingArrow
          ref={arrowRef}
          context={context as Parameters<typeof FloatingArrow>[0]['context']}
          className="fill-black"
        />
      </div>
    </FloatingPortal>
  );
}
